<template>
  <hb-basic-page
    :title="$tc('sites.title', 2)"
    :loading="loading && !subtleLoading"
    :fill-height="loading"
    :extension-height="60"
    :search-title="`${$t('sites.search')}...`"
    :initial-search="searchPhrase"
    class="worksites-list"
    @handle-search="handleSearch"
  >
    <template v-slot:app-bar-extension>
      <v-btn class="mb-3 ml-n1" @click="createSite" color="primary" v-if="$vuetify.breakpoint.smAndUp && $isTenantAdmin">
        <v-icon left class="no-gradient">mdi-plus</v-icon>
        <span>{{ $t('sites.new_site.buttons.add_new_work_site') }}</span>
      </v-btn>

      <v-btn
        class="mb-3 ml-2"
        @click="downloadDimensionCSV"
        color="primary"
        v-if="$vuetify.breakpoint.smAndUp && $isTenantAdmin"
      >
        <v-icon left class="no-gradient">mdi-download-outline</v-icon>
        <span>{{ $t('sites.download_dimension_csv') }}</span>
      </v-btn>
    </template>
    <v-col cols="12" class="gradient-icons">
      <v-fade-transition>
        <v-row no-gutters class="gradient-icons">
          <v-col
            v-if="canFilter"
            style="
              position: fixed;
              z-index: 99;
              width: calc(100vw);
              background-color: #fff;
              margin-top: -11px;
              margin-left: -12px;
              padding: 10px;
            "
            cols="12"
            class="elevation-2"
          >
            <v-row :no-gutters="$vuetify.breakpoint.smAndDown" align="center">
              <v-col cols="12" sm="4" md="2" lg="2" :class="$vuetify.breakpoint.smAndDown ? 'pb-0' : ''">
                <v-select
                  v-model="selectedStatuses"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  multiple
                  dense
                  solo
                  single-line
                  hide-details
                  prepend-inner-icon="filter_list"
                  :label="$t('sites.status')"
                >
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0" class="text-caption">{{ item.text }}</span>
                    <span v-else-if="index === 1" class="text-caption grey--text text--darken-1 ml-1">+{{ selectedStatuses.length - 1 }}</span>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" sm="6" md="3" class="site-toggle" :class="[{ 'mt-2': $vuetify.breakpoint.smAndDown }]">
                <v-btn-toggle v-model="viewMode" mandatory dense style="width: 100%" class="elevation-3">
                  <v-btn value="list" height="38" style="width: 50%" class="text-caption">
                    {{ $t('sites.view_list') }}
                  </v-btn>
                  <v-btn value="timeline" height="38" style="width: 50%" class="text-caption">
                    {{ $t('sites.view_timeline') }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>

              <v-col v-if="viewMode === 'timeline'" cols="12" sm="4" md="2" lg="2" :class="$vuetify.breakpoint.smAndDown ? 'pb-0' : ''">
                <date-picker
                  :input-styles="{
                    outlined: true,
                    dense: true,
                    solo: true,
                    singleLine: true,
                    hideDetails: true,
                    prefix: $t('sites.from'),
                    class: 'site-month-picker',
                  }"
                  type="month"
                  class="site-month-picker"
                  :date="startDate"
                  prependInnerIcon="mdi mdi-calendar"
                  append-icon="filter_list"
                  :label="$t('sites.planned_start_date_starts')"
                  @change="onChangeStartDate"
                />
              </v-col>

              <v-col cols="12" sm="6" md="2" :class="[{ 'mt-2': $vuetify.breakpoint.smAndDown }]">
                <v-btn text small @click="resetFilters">
                  <v-icon left small>mdi-filter-remove-outline</v-icon>
                  {{ $t('sites.clear_filters') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="canFilter" cols="12" class="filter-padding" :class="[viewMode === 'list' && 'filter-padding-timed-toggle']"> </v-col>
          <v-col cols="12" class="mt-5" v-if="loading || subtleLoading">
            <hb-loading-indicator align-middle />
          </v-col>

          <v-col v-if="!subtleLoading && viewMode === 'timeline'" cols="12" :class="canFilter ? 'mt-15' : 'mt-3'">
            <v-container class="pt-0" :pa-0="$vuetify.breakpoint.smAndDown" fluid>
              <v-row no-gutters :class="$vuetify.breakpoint.smAndDown ? 'pa-0 ma-0' : ''">
                <v-col cols="12" :class="$vuetify.breakpoint.smAndDown ? 'pa-0 ma-0' : ''">
                  <v-fade-transition hide-on-leave>
                    <v-row no-gutters>
                      <v-col cols="12">
                        <div v-if="datedSites.length > 0">
                          <v-timeline :light="searchPhrase.length > 0" two-line color="transparent" class="pt-0" dense align-top>
                            <template v-for="week in totalWeeksToShow">
                              <week-list-item-for-all-sites
                                :key="week.id"
                                :ref="`${week.id}`"
                                :week="week"
                                :filteredSites="datedSites"
                                :searchPhrase="searchPhrase"
                                :showOnlyFilledWeeks="searchPhrase.length > 0"
                              />
                            </template>
                          </v-timeline>
                        </div>
                        <v-container v-else pa-0 fluid>
                          <v-row no-gutters>
                            <v-col cols="12">
                              <v-row justify="center" align="center" no-gutters>
                                <div
                                  class="text-center pl-5 pr-5"
                                  style="padding-bottom: 150px"
                                  :style="{ paddingTop: $vuetify.breakpoint.smAndUp ? '200px' : '50px' }"
                                >
                                  <v-icon size="100">mdi mdi-shovel</v-icon>
                                  <div class="headline font-weight-bold mt-3 mb-3">{{ $t('sites.no_sites') }}</div>
                                  <div class="subtitle-1">{{ $t('sites.no_sites_help_for_tenant') }}</div>
                                  <v-btn @click="reloadSites" class="mt-5" outlined><v-icon left>refresh</v-icon> {{ $t('reload') }}</v-btn>
                                </div>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-col>
                    </v-row>
                  </v-fade-transition>
                </v-col>
              </v-row>
            </v-container>
          </v-col>

          <v-col v-if="!subtleLoading && viewMode === 'list'" cols="12" :class="canFilter ? 'mt-15' : 'mt-3'">
            <v-container class="pt-0" :pa-0="$vuetify.breakpoint.smAndDown" fluid>
              <v-row no-gutters :class="$vuetify.breakpoint.smAndDown ? 'pa-0 ma-0' : ''">
                <v-col cols="12" :class="$vuetify.breakpoint.smAndDown ? 'pa-0 ma-0' : ''">
                  <v-fade-transition hide-on-leave>
                    <v-row no-gutters>
                      <v-col cols="12">
                        <div v-if="filteredSites.length > 0">
                          <v-data-table
                            :headers="headers"
                            :items="filteredSites"
                            :items-per-page.sync="itemsPerPage"
                            :footer-props="{ itemsPerPageOptions: [10, 50, 100] }"
                            :custom-sort="sortFavoritesFirst"
                            class="elevation-1"
                            @click:row="item => goToSite(item)"
                          >
                            <template v-slot:item.plannedStartDate="{ item }">
                              {{ $formatDateNoTime(item.plannedStartDate) }}
                            </template>
                            <template v-slot:item.name="{ item }">
                              <site-favorite-button :site="item" small class="mr-1" />
                              <router-link :to="{ name: 'tenant_site_edit_site', params: { id: item.id.toString(), tenantId: $tenant } }">{{
                                [item.project && item.project.code, item.name].filter(Boolean).join(' ')
                              }}</router-link>
                            </template>
                            <template v-slot:item.plannedDuration="{ item }">
                              <v-chip v-if="item.plannedDuration" color="primary" small>{{ item.plannedDuration }} pv</v-chip>
                            </template>
                            <template v-slot:item.status="{ item }">
                              <v-chip
                                v-if="item.status"
                                :color="statusColor(item).color"
                                :text-color="statusColor(item).textColor"
                                style="cursor: pointer"
                                small
                                @click.stop="onStatusChipClick(item)"
                                >{{ item.status === null ? $t(`sites.statuses.no_status`) : $t(`sites.statuses.${item.status}`) }}
                              </v-chip>
                            </template>
                            <template v-slot:header.daybookCount>
                              Päiväkirja-<br>merkinnät
                            </template>
                            <template v-slot:item.daybookCount="{ item }">
                              {{ item.daybookCount || 0 }}
                            </template>
                            <template v-slot:header.latestDaybookDate>
                              Viimeisin<br>merkintä
                            </template>
                            <template v-slot:item.latestDaybookDate="{ item }">
                              {{ $formatDateNoTime(item.latestDaybookDate) }}
                            </template>
                          </v-data-table>
                        </div>
                        <v-container v-else pa-0 fluid>
                          <v-row no-gutters>
                            <v-col cols="12">
                              <v-row justify="center" align="center" no-gutters>
                                <div
                                  class="text-center pl-5 pr-5"
                                  style="padding-bottom: 150px"
                                  :style="{ paddingTop: $vuetify.breakpoint.smAndUp ? '200px' : '50px' }"
                                >
                                  <v-icon size="100">mdi mdi-shovel</v-icon>
                                  <div class="headline font-weight-bold mt-3 mb-3">{{ $t('sites.no_sites') }}</div>
                                  <div class="subtitle-1">{{ $t('sites.no_sites_help_for_tenant') }}</div>
                                  <v-btn @click="reloadSites" class="mt-5" outlined><v-icon left>refresh</v-icon> {{ $t('reload') }}</v-btn>
                                </div>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-col>
                    </v-row>
                  </v-fade-transition>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
          <v-btn fab fixed bottom right v-if="$vuetify.breakpoint.xsOnly && $isTenantAdmin" @click="createSite" color="primary">
            <v-icon class="mr-0 no-gradient">mdi-plus</v-icon>
          </v-btn>

          <v-dialog v-model="statusDialog" max-width="420" persistent>
            <v-card>
              <v-toolbar dark color="primary">
                <v-toolbar-title class="white--text">{{ $t('sites.change_status_title') }}</v-toolbar-title>
              </v-toolbar>
              <v-card-text class="pt-5">
                <div class="subtitle-1 font-weight-medium mb-4">{{ statusDialogSiteName }}</div>
                <v-select
                  v-model="statusDialogValue"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  :label="$t('sites.status')"
                  outlined
                  dense
                  hide-details
                />
              </v-card-text>
              <v-divider />
              <v-card-actions :class="$vuetify.breakpoint.xsOnly && 'flex-column-reverse'">
                <v-spacer />
                <v-btn :block="$vuetify.breakpoint.xsOnly" text :disabled="statusSaving" @click="statusDialog = false">{{ $t('cancel') }}</v-btn>
                <v-btn
                  :block="$vuetify.breakpoint.xsOnly"
                  :class="$vuetify.breakpoint.xsOnly && 'mb-2'"
                  color="primary"
                  :loading="statusSaving"
                  :disabled="!statusDialogValue"
                  @click="saveStatus"
                  >{{ $t('save') }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>
      </v-fade-transition>
    </v-col>
  </hb-basic-page>
</template>

<script>
import WeekListItemForAllSites from '@/components/Sites/WeekListItemForAllSites';
import SiteFavoriteButton from '@/components/Sites/SiteFavoriteButton';
import siteApi from '@/api/site';
import dayjs from 'dayjs';
import DatePicker from '@/components/DatePicker';
import workerApi from '@/api/worker';
import downloadjs from 'downloadjs';
import { sortItems } from 'vuetify/lib/util/helpers';

const isoWeek = require('dayjs/plugin/isoWeek');
dayjs.extend(isoWeek);

const STATUS_STORAGE_KEY = 'siteStatusFilter';
const VIEW_STORAGE_KEY = 'siteViewMode';
const SEARCH_STORAGE_KEY = 'siteSearchPhrase';
const ITEMS_PER_PAGE_STORAGE_KEY = 'siteListItemsPerPage';
const DEFAULT_STATUSES = ['CREATED', 'INPROGRESS'];
const ITEMS_PER_PAGE_OPTIONS = [10, 50, 100];

export default {
  name: 'WorkSitesView',
  components: { DatePicker, WeekListItemForAllSites, SiteFavoriteButton },
  data() {
    return {
      thisWeek: false,
      options: {
        duration: 500,
        offset: this.$vuetify.breakpoint.mdAndUp ? 60 : 130,
        easing: 'easeInOutCubic',
      },
      loading: true,
      subtleLoading: false,
      viewMode: 'list',
      selectedStatuses: [...DEFAULT_STATUSES],
      searchPhrase: '',
      itemsPerPage: 50,
      sites: [],
      startDate: dayjs().add(-3, 'months'),
      isUserRentOrEntrepreuner: false,
      statusDialog: false,
      statusDialogSite: null,
      statusDialogValue: null,
      statusSaving: false,
    };
  },

  computed: {
    canFilter() {
      return this.$isTenantAdmin || this.$isTenantManager;
    },
    statusOptions() {
      // Vain tenant_admin saa valita DONE/BILLED/DELETED; muut näkevät vain keskeneräiset.
      const values = this.$isTenantAdmin ? ['CREATED', 'INPROGRESS', 'DONE', 'BILLED', 'DELETED'] : [...DEFAULT_STATUSES];
      return values.map(v => ({ text: this.$t(`sites.statuses.${v}`), value: v }));
    },
    statusDialogSiteName() {
      const site = this.statusDialogSite;
      if (!site) return '';
      return [site.project && site.project.code, site.name].filter(Boolean).join(' ');
    },
    headers() {
      return [
        { text: 'Työmaa', value: 'name' },
        { text: 'Tilaaja', value: 'project.orderer.companyName' },
        { text: 'Pääurakoitsija', value: 'project.primeContractor.companyName' },
        { text: 'Työryhmä', value: 'workGroup.name' },
        { text: 'Tila', value: 'status' },
        { text: 'Suunniteltu aloitus', value: 'plannedStartDate' },
        { text: 'Arvioitu kesto', value: 'plannedDuration' },
        { text: 'Päiväkirjamerkinnät', value: 'daybookCount', sortable: false, align: 'center' },
        { text: 'Viimeisin merkintä', value: 'latestDaybookDate', sortable: false },
      ];
    },
    filteredSites() {
      if (!this.searchPhrase || this.searchPhrase.length === 0) return this.sites;
      const splitFilterText = this.searchPhrase.toLowerCase().split(' ');
      return this.sites.filter(k => {
        const siteName = [k.project?.code, k.name, k.project?.primeContractor?.companyName, k.project?.orderer?.companyName]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return splitFilterText.every(word => siteName.indexOf(word) > -1);
      });
    },
    datedSites() {
      return this.filteredSites
        .filter(s => s.plannedStartDate)
        .sort((a, b) => {
          if (a.plannedStartDate.isBefore(b.plannedStartDate)) return -1;
          if (a.plannedStartDate.isAfter(b.plannedStartDate)) return 1;
          return 0;
        });
    },
    latestWeekToShow() {
      if (this.datedSites.length > 0) {
        const moments = this.datedSites.map(t => t.plannedStartDate).filter(Boolean).map(d => dayjs(d));
        const maxDate = moments.reduce((accumulator, curVal) => (curVal && accumulator.isAfter(curVal) ? accumulator : curVal));
        return dayjs(maxDate).add(2, 'weeks');
      }
      return dayjs().endOf('year');
    },
    totalWeeksToShow() {
      let listOfWeeks = [];
      const currentDate = dayjs(this.startDate);
      const weekDiff = this.latestWeekToShow.diff(currentDate, 'weeks');

      for (let i = 0; i < weekDiff; i++) {
        let weekStart = currentDate.clone().add(i, 'weeks').startOf('isoWeek');
        let weekEnd = currentDate.clone().add(i, 'weeks').endOf('isoWeek');

        listOfWeeks.push({
          week: dayjs(weekStart).isoWeek(),
          id: weekStart.format('W/YYYY'),
          weekStartDate: weekStart.format('DD.MM.YYYY'),
          weekEndDate: weekEnd.format('DD.MM.YYYY'),
        });
      }
      return listOfWeeks;
    },
  },
  methods: {
    // Suosikit aina ensin valitusta järjestyssarakkeesta riippumatta; kumpikin ryhmä järjestetään Vuetifyn omalla
    // järjestyksellä. Ajetaan ennen sivutusta, joten suosikit ovat ensimmäisellä sivulla.
    sortFavoritesFirst(items, sortBy, sortDesc, locale, customSorters) {
      const favorites = items.filter(i => i.favorite);
      const others = items.filter(i => !i.favorite);
      return [
        ...sortItems(favorites, sortBy, sortDesc, locale, customSorters),
        ...sortItems(others, sortBy, sortDesc, locale, customSorters),
      ];
    },
    async downloadDimensionCSV() {
      try {
        this.subtleLoading = true;
        const csv = await siteApi.downloadDimensionCSV();
        const filename = 'tyomaa_dimension_nimikkeet.csv';
        downloadjs(csv, filename, 'text/csv');
        this.$showSuccessNotification(this.$t('sites.dimension_csv_downloaded'));
      } catch (error) {
        console.error(error);
        this.$showErrorNotification(this.$t('errors.error_downloading_csv'));
      } finally {
        this.subtleLoading = false;
      }
    },
    statusColor(site) {
      switch (site.status) {
        case 'CREATED':
          return { color: 'light-blue', textColor: 'white' };
        case 'SCHEDULED':
          return { color: 'blue', textColor: 'white' };
        case 'INPROGRESS':
          return { color: 'orange', textColor: 'white' };
        case 'DONE':
          return { color: 'green', textColor: 'white' };
        default:
          return { color: 'pink', textColor: 'white' };
      }
    },
    goToSite(site) {
      this.$router.push({ name: 'tenant_site_edit_site', params: { id: site.id.toString(), tenantId: this.$tenant } });
    },
    onStatusChipClick(site) {
      // Tilatietoa saa muuttaa vain tenant_admin; muut päätyvät työmaan näkymään kuten rivilläkin.
      if (this.$isTenantAdmin) {
        this.openStatusDialog(site);
      } else {
        this.goToSite(site);
      }
    },
    openStatusDialog(site) {
      this.statusDialogSite = site;
      this.statusDialogValue = site.status;
      this.statusDialog = true;
    },
    async saveStatus() {
      try {
        this.statusSaving = true;
        await siteApi.updateSiteStatus(this.statusDialogSite.id, this.statusDialogValue);
        this.statusDialog = false;
        this.$showSuccessNotification(this.$t('sites.status_changed'));
        await this.reloadSites();
      } catch (error) {
        console.log(error);
        this.$showErrorNotification(this.$t('sites.status_change_failed'), false);
      } finally {
        this.statusSaving = false;
      }
    },
    async getWorker() {
      const worker = await workerApi.getWorker(this.$userInfo.id);
      if (worker.relation === 'entrepreneur' || worker.relation === 'rent') {
        this.isUserRentOrEntrepreuner = true;
      } else {
        this.isUserRentOrEntrepreuner = false;
      }
    },
    onChangeStartDate(e) {
      if (dayjs(e).isValid()) {
        this.startDate = dayjs(e).startOf('month');
        this.reloadSites();
      }
    },
    handleSearch(searchPhrase) {
      this.searchPhrase = searchPhrase;
    },
    createSite() {
      this.$router.push({ name: 'tenant_site_new_site' });
    },
    resetFilters() {
      this.selectedStatuses = [...DEFAULT_STATUSES];
      this.searchPhrase = '';
      this.startDate = dayjs().add(-3, 'months');
      this.viewMode = 'list';
      localStorage.removeItem(STATUS_STORAGE_KEY);
      localStorage.removeItem(VIEW_STORAGE_KEY);
      localStorage.removeItem(SEARCH_STORAGE_KEY);
      this.reloadSites();
    },
    restoreFiltersFromStorage() {
      const storedStatuses = localStorage.getItem(STATUS_STORAGE_KEY);
      if (storedStatuses) {
        try {
          const parsed = JSON.parse(storedStatuses);
          if (Array.isArray(parsed) && parsed.length > 0) this.selectedStatuses = parsed;
        } catch (e) {
          // virheellinen arvo localStoragessa – käytetään oletusta
        }
      }
      const storedView = localStorage.getItem(VIEW_STORAGE_KEY);
      if (storedView === 'timeline' || storedView === 'list') {
        this.viewMode = storedView;
      }
      const storedSearch = localStorage.getItem(SEARCH_STORAGE_KEY);
      if (storedSearch) {
        this.searchPhrase = storedSearch;
      }
      const storedItemsPerPage = parseInt(localStorage.getItem(ITEMS_PER_PAGE_STORAGE_KEY), 10);
      if (ITEMS_PER_PAGE_OPTIONS.includes(storedItemsPerPage)) {
        this.itemsPerPage = storedItemsPerPage;
      }
    },
    async reloadSites() {
      let loadingTimeout = null;
      if (this.sites.length > 0) {
        loadingTimeout = setTimeout(() => {
          this.subtleLoading = true;
        }, 300);

        this.loading = false;
      } else {
        this.subtleLoading = false;
        this.loading = true;
      }

      try {
        const sites = await siteApi.getSitesWithFilters({
          statuses: this.selectedStatuses,
          startDate: this.viewMode === 'timeline' ? this.startDate : null,
        });
        this.sites = sites;
        this.loading = false;
      } catch (error) {
        console.log(error);
        this.$showErrorNotification(this.$t('errors.error_loading_sites'), false);
      }
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
      this.loading = false;
      this.subtleLoading = false;
    },
  },
  watch: {
    viewMode(val) {
      localStorage.setItem(VIEW_STORAGE_KEY, val);
      this.reloadSites();
    },
    selectedStatuses(val) {
      localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify(val));
      this.reloadSites();
    },
    searchPhrase(val) {
      val ? localStorage.setItem(SEARCH_STORAGE_KEY, val) : localStorage.removeItem(SEARCH_STORAGE_KEY);
    },
    itemsPerPage(val) {
      localStorage.setItem(ITEMS_PER_PAGE_STORAGE_KEY, val);
    },
  },
  async mounted() {
    this.loading = true;
    this.restoreFiltersFromStorage();
    await this.getWorker();
    if (!this.canFilter) {
      this.viewMode = 'list';
    }
    await this.reloadSites();
    this.thisWeek = dayjs().format('W/YYYY');

    if (this.$refs[this.thisWeek] && this.$refs[this.thisWeek][0]) {
      await this.$vuetify.goTo(this.$refs[this.thisWeek][0], this.options);
    }
    this.loading = false;
  },
};
</script>

<style lang="scss">
@import '~vuetify/src/styles/settings/variables';
.filter-padding {
  padding-bottom: 30px !important;
}
@media #{map-get($display-breakpoints,'sm-and-down')} {
  .filter-padding {
    padding-bottom: 170px !important;
  }
  .filter-padding-timed-toggle {
    padding-bottom: 70px !important;
  }
  .worksites-list {
    .v-timeline-item {
      padding-bottom: 0;
    }
    .v-timeline {
      &:before {
        display: none !important;
      }

      &-item__body {
        max-width: 100% !important;

        .v-card {
          &:before,
          &:after {
            top: 0 !important;
            border: 0 !important;
          }
        }
      }

      .v-timeline-item__divider {
        display: none;
      }
    }
  }
}
.worksites-list {
  .site-toggle {
    .v-btn__content {
      color: #000;
    }
    .v-btn--active {
      background-color: var(--v-primary-base) !important;
      .v-btn__content {
        color: #fff;
      }
    }
  }
  .site-month-picker {
    .v-input__slot {
      height: 38px !important;
    }
    fieldset {
      color: transparent !important;
      &:hover {
        color: transparent !important;
      }
    }
  }
}
</style>
